import {computed, inject, Injectable, signal} from '@angular/core';
import {Subscription} from 'rxjs';
import {tap} from 'rxjs/operators';
import {CalendarAppointment, Events, formatPatientName, Utilities, WebSocketService} from '@xmedical/core';
import {EventInput} from '@fullcalendar/core';
import {CALENDAR_DATA_PROVIDER} from '../tokens/calendar.tokens';

@Injectable()
export class CalendarStore {
  private dataProvider = inject(CALENDAR_DATA_PROVIDER);
  private websocket = inject(WebSocketService);

  // State signals
  readonly appointments = signal<CalendarAppointment[]>([]);
  readonly loading = signal<boolean>(false);

  // Current query params for refresh
  private currentStartDate = '';
  private currentEndDate = '';
  private currentLocationId: number | null = null;
  private currentPractitionerId: number | null = null;

  // WebSocket
  private subscribedChannelNames: string[] = [];
  private wsCallback: ((data: unknown) => void) | null = null;

  // Subscription tracking
  private loadSubscription: Subscription | null = null;

  // Computed: transform Appointment[] to FullCalendar EventInput[]
  readonly calendarEvents = computed<EventInput[]>(() => {
    const format = this.dataProvider.practiceSettings()?.defaultPatientDisplayFormat;
    return this.appointments().map(appointment => {
      const color = appointment.appointmentType?.displayStyle ?? undefined;
      const rgba = color ? Utilities.hexToRgba(color, 0.9) : null;
      const bgColor = rgba ? `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})` : undefined;
      const patientName = formatPatientName(appointment.patient, format);
      return {
        id: String(appointment.id),
        title: patientName || appointment.title || 'Appointment',
        start: appointment.start,
        end: appointment.end ?? undefined,
        backgroundColor: bgColor,
        borderColor: color,
        extendedProps: {appointment}
      };
    });
  });

  loadAppointments(startDate: string, endDate: string, locationId: number | null, practitionerId: number | null): void {
    this.currentStartDate = startDate;
    this.currentEndDate = endDate;
    this.currentLocationId = locationId;
    this.currentPractitionerId = practitionerId;

    this.loading.set(true);
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.dataProvider
      .loadAppointments(startDate, endDate, locationId, practitionerId)
      .pipe(
        tap(appointments => {
          this.appointments.set(appointments);
          this.loading.set(false);
        })
      )
      .subscribe({
        error: () => this.loading.set(false)
      });
  }

  refreshAppointments(): void {
    if (this.currentStartDate && this.currentEndDate) {
      this.loadAppointments(
        this.currentStartDate,
        this.currentEndDate,
        this.currentLocationId,
        this.currentPractitionerId
      );
    }
  }

  subscribeToWebSocket(): void {
    const channelNames = this.dataProvider.getEventChannelNames();
    this.wsCallback = () => this.refreshAppointments();
    channelNames.forEach(channelName => {
      this.websocket.subscribe(channelName, Events.AppointmentAppointmentChanged, this.wsCallback);
      this.websocket.subscribe(channelName, Events.AppointmentAppointmentFlagsChanged, this.wsCallback);
    });
    this.subscribedChannelNames = channelNames;
  }

  unsubscribeFromWebSocket(): void {
    if (this.wsCallback) {
      this.subscribedChannelNames.forEach(channelName => {
        this.websocket.unsubscribe(channelName, Events.AppointmentAppointmentChanged, this.wsCallback);
        this.websocket.unsubscribe(channelName, Events.AppointmentAppointmentFlagsChanged, this.wsCallback);
      });
      this.wsCallback = null;
      this.subscribedChannelNames = [];
    }
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = null;
  }
}
